import { SerializedData, Store, timeoutPromise } from "bernie-core";
import { action, observable, toJS } from "mobx";
import {
  Composition,
  ExtendedContextStore,
  FlexContext,
  TrackingContext,
  ViewModelResponse,
  FlexBffTemplateResponse,
  AjaxParams,
  ModuleAjaxParams,
  Template,
  LoggerOptions,
} from "typings/flexFramework/FlexDefinitions";
import * as fetch from "isomorphic-fetch";
import { safeResponseToJson } from "components/utility/FetchUtils";
import { Logger, NOOP_LOGGER } from "bernie-logger";
import { FlexComponent } from "typings/flexFramework/FlexComponent";
import {
  FAILED_REACTIVE_MODULES_COMPOSITION_FETCH,
  FAILED_MODULE_COMPOSITION_FETCH,
  FAILED_COMPOSITION_FETCH,
  FAILED_COMPOSITION_PARSE,
} from "config/systemEvents";
import { ContentFlexComponent } from "components/flexComponents/Content/typings";
import { getQueryParamValue } from "components/utility/FeatureFlag/UrlUtils";
import { StaticMapStore } from "stores/staticMap/StaticMapStore";
import { FlexModuleModel, FlexModuleModelStore } from "src/stores/FlexModuleModelStore";
import * as jsonBigInt from "json-bigint";
import { getNameFromFlexComponent } from "./getNameFromFlexComponent";
import { BlossomComponentTreeStore, NewBlossomComponent } from "stores/BlossomComponentTree";

const jsonBigIntParse = jsonBigInt({ storeAsString: true }).parse;

export class CompositionStore extends Store {
  @observable
  public composition?: Composition;

  private blossomComponentsLogger: Logger;
  private flexModuleModelStore: FlexModuleModelStore;

  public pageHeading: string | null;
  public pageSubHeadline: string | null;
  public title: string;
  public templateId: string;
  public canonicalPath: string;

  private flexContext?: FlexContext;
  private trackingContext?: TrackingContext;
  public bffHostname: string;
  public bffTimeout: number;

  public isInFlexEnableGraphqlModulesTest = false; // set to true by FlexController if in correct test bucket, will be false for composition based rendering in all cases.

  public constructor(
    state: SerializedData = {},
    logger: Logger = NOOP_LOGGER,
    blossomComponentLogger: Logger,
    config: any = {}
  ) {
    super(state, logger);
    this.blossomComponentsLogger = blossomComponentLogger;
    this.bffHostname = config?.lpt?.url ?? "https://lpt-blossom-bff.us-west-2.prod.expedia.com";
    this.bffTimeout = config?.timeout ?? 5000;
  }

  /* istandbul ignore next */
  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }

  /**
   * Initializes CompositionStore with data from other stores. Called from controller
   * @param  {ViewModelResponse|FlexBffTemplateResponse} bffResponse
   * @param  {ExtendedContextStore} context
   * @param  {FlexModuleModelStore} flexModuleModelStore
   * @param  {BlossomComponentTreeStore} blossomComponentTreeStore
   * @param  {StaticMapStore} mapStore
   * @returns void
   */
  @action
  public setup(
    bffResponse: ViewModelResponse | FlexBffTemplateResponse,
    context: ExtendedContextStore,
    flexModuleModelStore: FlexModuleModelStore,
    blossomComponentTreeStore: BlossomComponentTreeStore,
    mapStore: StaticMapStore
  ) {
    if (!bffResponse) {
      return;
    }

    this.flexModuleModelStore = flexModuleModelStore;

    const isCompositionResponse = (
      response: ViewModelResponse | FlexBffTemplateResponse
    ): response is ViewModelResponse => {
      return !!(response as ViewModelResponse).composition;
    };

    if (isCompositionResponse(bffResponse)) {
      this.composition = bffResponse.composition;
      this.pageHeading = bffResponse.composition.pageHeading;
      this.pageSubHeadline = bffResponse.composition.pageSubHeadline;
      this.title = bffResponse.composition.title;

      const newTreeFromComposition = this.composition.page.regionList.reduce((result, child) => {
        const childBlossomComponent = this.flexComponentToBlossomComponent(
          child,
          flexModuleModelStore,
          blossomComponentTreeStore,
          context
        );
        if (childBlossomComponent !== undefined) {
          result.push(childBlossomComponent);
        }

        return result;
      }, [] as NewBlossomComponent[]);

      blossomComponentTreeStore.setTree(newTreeFromComposition);
    }

    this.templateId = bffResponse.templateId;
    this.canonicalPath = bffResponse.canonicalPath;
    this.flexContext = bffResponse.flexContext;
    this.trackingContext = bffResponse.trackingContext;
    this.injectInterestFreePayLater();
    this.buildStaticMapImgUrl(context, mapStore, flexModuleModelStore);
  }

  /**
   * Populates FlexModuleModelStore from the composition. Necessary to fully reconstruct state after hydration as some data is omitted from serialization. See CompositionStore.toJSON() for context
   * @param {FlexModuleModelStore} flexModuleModelStore
   * @param {BlossomComponentTreeStore} blossomComponentTreeStore
   * @param {ExtendedContextStore} context
   */
  @action
  public populateFlexModuleModelStoreFromComposition(
    flexModuleModelStore: FlexModuleModelStore,
    blossomComponentTreeStore: BlossomComponentTreeStore,
    context: ExtendedContextStore
  ) {
    this.flexModuleModelStore = this.flexModuleModelStore ?? flexModuleModelStore;
    if (flexModuleModelStore.modelMap.size > 0 || !this.composition) {
      // modelMap is already populated
      return;
    }

    this.composition.page.regionList.forEach((region) => {
      // flexModuleModelStore is populated in the BlossomComponent constructor
      this.flexComponentToBlossomComponent(region, flexModuleModelStore, blossomComponentTreeStore, context);
    });
  }

  /**
   * creates NewBlossomComponent equivalent of provided FlexComponent
   * @param  {FlexComponent} flexComponent
   * @param  {FlexModuleModelStore} flexModuleModelStore
   * @param  {BlossomComponentTreeStore} blossomComponentTreeStore
   * @param  {ExtendedContextStore} context
   * @returns NewBlossomComponent|undefined
   */
  private flexComponentToBlossomComponent(
    flexComponent: FlexComponent,
    flexModuleModelStore: FlexModuleModelStore,
    blossomComponentTreeStore: BlossomComponentTreeStore,
    context: ExtendedContextStore
  ): NewBlossomComponent | undefined {
    const name = getNameFromFlexComponent(flexComponent);
    const id = flexComponent.type === "Module" ? flexComponent.id : flexComponent.model?.id;

    // gather and create decendant BlossomComponents
    const childFlexComponents: FlexComponent[] = [
      ...(flexComponent.modules || []),
      ...(flexComponent.groups || []),
      ...(flexComponent.children || []),
    ];
    const children = childFlexComponents.reduce((result, child) => {
      const childBlossomComponent = this.flexComponentToBlossomComponent(
        child,
        flexModuleModelStore,
        blossomComponentTreeStore,
        context
      );
      if (childBlossomComponent !== undefined) {
        result.push(childBlossomComponent);
      }

      return result;
    }, [] as NewBlossomComponent[]);

    //TODO: what should responsePending be for regions, tab groups, tabs?
    return new NewBlossomComponent({
      componentData: {
        id,
        name,
        fmId: flexComponent.fmId,
        fmTitleId: flexComponent.fmTitleId,
        view: flexComponent.model?.view,
        scene: flexComponent.model?.scene,
        children,
        flexModuleModel: this.buildFlexModuleModel(flexComponent.model),
      },
      context,
      flexModuleModelStore,
      blossomComponentTreeStore,
      logger: this.blossomComponentsLogger,
    });
  }

  private buildFlexModuleModel = (model: any): FlexModuleModel => ({
    responsePending: !model || model.ajaxOnly ? true : false,
    empty: !!model?.empty,
    error: !!model?.error,
    model,
  });

  /**
   * Fetches a Flex Composition from FlexBFF, populating the CompositionStore with a composition and a populated moduleMap
   * @param template a Flex template
   * @param {ExtendedContextStore} context
   * @param {FlexModuleModelStore} flexModuleModelStore
   * @param {BlossomComponentTreeStore} blossomComponentTreeStore
   * @param headers
   * @returns Promise<void>
   */
  @action
  public fetchCompositionFromTemplate = (
    template: Template,
    context: ExtendedContextStore,
    flexModuleModelStore: FlexModuleModelStore,
    blossomComponentTreeStore: BlossomComponentTreeStore,
    headers: any
  ) =>
    new Promise((resolve, reject) => {
      const requestBody = {
        flexContext: this.flexContext,
        template,
        trackingContext: this.trackingContext,
        templateId: this.templateId,
      };

      const fetchOptions: RequestInit = {
        method: "POST",
        headers: {
          ...headers,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        credentials: "include",
      };

      timeoutPromise(
        fetch(`${this.bffHostname}/bff/composition`, fetchOptions),
        this.bffTimeout,
        "composition fetch timeout. Retrying."
      )
        .then((response) => response.text())
        .then((body) => {
          try {
            this.composition = jsonBigIntParse(body).composition;
            this.populateFlexModuleModelStoreFromComposition(flexModuleModelStore, blossomComponentTreeStore, context);
            resolve();

            return;
          } catch (err) {
            this.logger.logEvent(
              FAILED_COMPOSITION_PARSE,
              "Unexpected error when parsing composition from template",
              err
            );
            throw err;
          }
        })
        .catch((err) => {
          this.logger.logEvent(
            FAILED_COMPOSITION_FETCH,
            "Unexpected error when fetching composition from template",
            err
          );
          reject(err);

          return;
        });
    });

  public buildStaticMapImgUrl = (
    context: ExtendedContextStore,
    mapStore: StaticMapStore,
    flexModuleModelStore: FlexModuleModelStore
  ) => {
    const hotelsModel = flexModuleModelStore.modelMap.get("hotels-1")?.model as any;

    if (hotelsModel) {
      mapStore.generateGoogleStaticMapSrcForHotels(context, hotelsModel.hotels);
    }
  };

  /**
   * fetches updated model from FlexBFF for module indicated in options
   * @param options
   * @param loggerOptions [optional]
   * @returns Promise<void>
   * @private
   * @memberof CompositionStore
   */
  private fetchModule = async (options: ModuleAjaxParams, loggerOptions: LoggerOptions) => {
    if (!options.fmId) {
      return Promise.reject("options.fmId is required");
    }

    options.fmId = options.fmId;
    options.url = this.buildUrlQueryParam();
    options.cache = options.cache ?? "false";
    options.epoch = options.epoch ?? Date.now().toString();
    options.viewName = options.viewName ?? "json";
    options.clientId = options.clientId ?? "blossom";
    options.userAttributes = options.userAttributes ?? "blossom";
    options.path = "/lpt/flex/ajax";
    options.tId = options.tId ?? this.templateId;

    const endpointUrl = this.buildAjaxEndpointUrl(options, loggerOptions);

    if (endpointUrl) {
      return fetch(endpointUrl, { method: "GET" }).then(safeResponseToJson);
    }
  };

  /**
   * fetches updated model from FlexBFF for module indicated in options
   * and updates the composition and FlexModuleModelStore with the response
   *
   * @param options
   * @param loggerOptions [optional]
   * @returns Promise<void>
   */
  public updateModule = async (options: ModuleAjaxParams, loggerOptions?: LoggerOptions) => {
    const _loggerOptions = loggerOptions ?? {
      event: FAILED_MODULE_COMPOSITION_FETCH,
      args: `Error when fetching composition for module due to missing values for templateId=${this.templateId}, fmId=${options.fmId}`,
    };

    return this.fetchModule(options, _loggerOptions)
      .then((moduleBody) => {
        if (moduleBody) {
          this.updateModuleModels(options.fmId, this.buildFlexModuleModel(moduleBody));
        }

        return Promise.resolve({
          status: 200,
        });
      })
      .catch((err) => {
        this.logger.logEvent(_loggerOptions.event, _loggerOptions.args, err);

        return Promise.resolve({
          status: 500,
        });
      });
  };

  /**
   * Fetches modules related to a scene change from the FlexBFF
   * and updates the composition and FlexModuleModelStore with the response
   *
   * @param scene a string id representing the scene attribute value
   * @returns Promise<void>
   */
  public changeScene = async (scene: string) => {
    return this.fetchScene(scene)
      .then((components: FlexComponent[]) => {
        if (this.composition) {
          components.forEach((c) => this.updateModuleModels(c.fmId, this.buildFlexModuleModel(c.model)));
        }
      })

      .catch((err) => {
        this.logger.logEvent(
          FAILED_MODULE_COMPOSITION_FETCH,
          "Unexpected error when fetching composition for scene",
          err
        );
      });
  };

  /**
   * Fetches modules related to a scene change from the FlexBFF
   *
   * @param sceneId a string id representing the scene attribute value
   * @returns Promise<void>
   */
  private fetchScene = async (sceneId: string) => {
    if (!sceneId) {
      return Promise.reject("sceneId is required");
    }

    const endpointUrl = this.buildAjaxEndpointUrl(
      {
        tId: this.templateId,
        scene: sceneId,
        path: "/lpt/flex/reactive",
      },
      {
        event: FAILED_REACTIVE_MODULES_COMPOSITION_FETCH,
        args: `Error when fetching reactive modules for scene due to missing values for templateId=${this.templateId}, scene=${sceneId}`,
      }
    );

    if (endpointUrl) {
      return fetch(endpointUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(this.flexContext),
      }).then(safeResponseToJson);
    }
  };

  /**
   * takes the model of the provided FlexComponent and updates the entries in Composition and FlexModuleModelStore indicated by the provided fmId
   *
   * @param fmId the fmId of the component to be updated
   * @param {FlexComponent | any} component a FlexComponent containing the new data
   */
  public updateModuleModels = (fmId: number, flexModuleModel: FlexModuleModel) => {
    const componentToUpdate = this.findFlexComponentByFmId(fmId);

    if (componentToUpdate && this.flexModuleModelStore) {
      componentToUpdate.model = flexModuleModel.model;
      this.flexModuleModelStore.updateModelMap(componentToUpdate.id, flexModuleModel);
    }

    return;
  };

  /**
   * Finds out if the region should be displayed or not based on: if all its children are ajax only then it will be hidden.
   *
   * @param regionFmId - the region's fmId
   * @returns boolean - should the region be displayed
   */
  public shouldDisplayRegion(regionFmId: number): boolean {
    let shouldDisplayRegion = false;
    const requestedRegion = this.findFlexComponentByFmId(regionFmId);
    if (requestedRegion) {
      requestedRegion.modules.forEach((module) => {
        if (module.model.ajaxOnly !== true) {
          shouldDisplayRegion = true;
        }
      });
    }

    return shouldDisplayRegion;
  }

  private buildAjaxEndpointUrl(options: AjaxParams, loggerOptions: LoggerOptions) {
    if (!options.tId || (!options.fmId && !options.scene)) {
      this.logger.logEvent(loggerOptions.event, loggerOptions.args, JSON.stringify(options));

      return;
    }

    let queryParams = "";
    Object.keys(options)
      .filter((key) => options[key])
      .forEach((key, index) => {
        if (key) {
          queryParams = index === 0 ? `?${key}=${options[key]}` : `${queryParams}&${key}=${options[key]}`;
        }
      });

    return `${options.path}${queryParams}`;
  }

  private buildUrlQueryParam(): string | undefined {
    if (this.canonicalPath) {
      const tName = getQueryParamValue("tName");
      let urlQueryParam = this.canonicalPath;

      if (this.canonicalPath === "/") {
        urlQueryParam = this.canonicalPath;
      } else if (this.canonicalPath.startsWith("/")) {
        urlQueryParam = this.canonicalPath.substr(1);
      }

      if (tName) {
        urlQueryParam = `${urlQueryParam}?tName=${tName}`;
      }

      return encodeURI(urlQueryParam);
    }

    return undefined;
  }

  // Temporary hack to add required EXP_MX (TPID 12) /EXP_BR (TPID 69) Legal & Loyalty Information
  // This assumes that the region is called Wizard and page type is HTG
  private injectInterestFreePayLater = () => {
    const tpid = this.flexContext?.siteContext.tpid;
    if (
      (tpid === 12 || tpid === 69) &&
      this.flexContext &&
      this.flexContext.searchContext &&
      this.flexContext.searchContext.pageType === "Travel-Guide-Hotels"
    ) {
      const wizardRegion =
        this.composition &&
        this.composition.page &&
        this.composition.page.regionList &&
        this.composition.page.regionList.filter((region) => region.name === "wizard")[0];

      if (wizardRegion) {
        const editorials: ContentFlexComponent[] = wizardRegion && wizardRegion.modules;
        const messaging = tpid === 12 ? "Hoteles en hasta 18 MSI" : "Hotéis em até 12x sem juros";

        const injectedflexComponent: ContentFlexComponent = {
          model: {
            contentPurpose: "ConfidenceMessage",
            items: [{ text: messaging }],
          },
          type: "Module",
          name: "editorial",
        } as any;
        wizardRegion.modules = [...editorials, injectedflexComponent];
      }
    }
  };

  /**
   * removes data that will be re-constructed client side
   * @returns SerializedData
   */
  public toJSON(): SerializedData {
    const keysToIgnore = [
      "flexContext",
      "trackingContext",
      "blossomComponentsLogger",
      "flexModuleModelStore",
      "bffHostname",
    ];
    if (this.isInFlexEnableGraphqlModulesTest) {
      return super.toJSON([...keysToIgnore, "composition"]);
    }
    //TODO after hybrid blossom refactor is complete, exclude composition from serialization

    return super.toJSON(keysToIgnore);
  }

  /**
   * finds FlexComponent associated with provided fmId, searching entire composition or provided composition fragment
   * @param fmId
   * @param componentsToSearch [optional]
   * @returns FlexComponent | undefined
   * @memberof CompositionStore
   */
  public findFlexComponentByFmId = (
    fmId: number,
    componentsToSearch?: FlexComponent | FlexComponent[]
  ): FlexComponent | undefined => {
    if (!fmId) {
      return;
    }

    const components = componentsToSearch ?? this.composition?.page.regionList;

    if (!components) {
      return;
    }

    return findFlexComponent(components, (c) => c.fmId === fmId);
  };
}

/**
 * returns first match in provided FlexComponent[] collection which satisfies the provided test, iterating downward through all child components
 * @export
 * @param {(FlexComponent | FlexComponent[])} componentsToSearch
 * @param {(flexComponent: FlexComponent) => boolean} test
 * @returns {(FlexComponent | undefined)}
 */
export function findFlexComponent(
  componentsToSearch: FlexComponent | FlexComponent[],
  test: (flexComponent: FlexComponent) => boolean
): FlexComponent | undefined {
  let result: FlexComponent | undefined;

  //Array.isArray doesn't work on MobX observableArray must convert to plain JS first
  const components = Array.isArray(toJS(componentsToSearch))
    ? (componentsToSearch as FlexComponent[])
    : [componentsToSearch as FlexComponent];

  for (const component of components) {
    if (test(component)) {
      result = component;
      break;
    }

    const childComponents: FlexComponent[] = [
      ...(component.modules || []),
      ...(component.groups || []),
      ...(component.children || []),
    ];

    if (childComponents) {
      result = findFlexComponent(childComponents, test);

      if (result) {
        break;
      }
    }
  }

  return result;
}
